// ============================================================
// Primitive card types
// ============================================================

export type Suit = "clubs" | "diamonds" | "hearts" | "spades";
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
// Rank: 1 = Ass, 11 = Bube, 12 = Dame, 13 = König
export type Color = "red" | "black";

export interface Card {
  readonly id: string;     // z.B. "hearts-1", "spades-13"
  readonly suit: Suit;
  readonly rank: Rank;
  readonly color: Color;
  readonly faceUp: boolean;
}

// ============================================================
// Stapel-Typen
// ============================================================

/** Geordnetes Karten-Array. Oberste Karte = letztes Element. */
export type CardPile = readonly Card[];

// ============================================================
// Zug-Typen (Discriminated Union)
// ============================================================

export interface MoveToTableau {
  readonly type: "MOVE_TO_TABLEAU";
  readonly cards: readonly Card[];   // Die Karte(n) die bewegt werden
  readonly fromPile: PileRef;
  readonly toColumnIndex: number;    // 0-6
}

export interface MoveToFoundation {
  readonly type: "MOVE_TO_FOUNDATION";
  readonly card: Card;
  readonly fromPile: PileRef;
  readonly toFoundationIndex: number; // 0-3
}

export interface DrawFromStock {
  readonly type: "DRAW_FROM_STOCK";
}

export interface FlipCard {
  readonly type: "FLIP_CARD";
  readonly tableauColumnIndex: number; // 0-6
}

export interface RecycleStock {
  readonly type: "RECYCLE_STOCK";
}

export type Move =
  | MoveToTableau
  | MoveToFoundation
  | DrawFromStock
  | FlipCard
  | RecycleStock;

// ============================================================
// Stapel-Referenzen (woher kommt eine Karte)
// ============================================================

export type PileRef =
  | { readonly kind: "waste" }
  | { readonly kind: "tableau"; readonly columnIndex: number }
  | { readonly kind: "foundation"; readonly foundationIndex: number };

// ============================================================
// Spielvariante: 1 oder 3 Karten ziehen
// ============================================================

export type DrawMode = 1 | 3;

// ============================================================
// Custom Layout — manuelle Karten-Anordnung
// ============================================================

/**
 * Beschreibt eine manuell definierte Startaufstellung.
 *
 * Karten-Format: "{farbe}-{wert}"
 *   Farbe:  H (Herz), K (Karo), P (Pik), Kr (Kreuz)
 *   Wert:   A, 2-10, B (Bube), D (Dame), Kg (König)
 *   Bsp:    "H-A", "K-3", "P-Kg", "Kr-B"
 *
 * Tableau: 7 Spalten, von unten nach oben.
 *   Letzte Karte = sichtbar, alle anderen verdeckt.
 *
 * Stock: Reihenfolge von oben — erste Karte in der Liste
 *   wird als erstes gezogen.
 */
export interface CustomLayout {
  readonly tableau: readonly [
    readonly string[],
    readonly string[],
    readonly string[],
    readonly string[],
    readonly string[],
    readonly string[],
    readonly string[],
  ];
  readonly stock: readonly string[];
}

// ============================================================
// Spielzustand (immutable — jede Änderung gibt neuen State zurück)
// ============================================================

export interface GameState {
  readonly gameId: string;
  readonly seed: number;
  readonly drawMode: DrawMode;
  /** Gesetzt wenn das Spiel aus einem CustomLayout gestartet wurde (für Undo-Replay). */
  readonly layoutSource?: CustomLayout;
  readonly stock: CardPile;         // Talon — verdeckt
  readonly waste: CardPile;         // Ablagestapel — offen (nur oberste spielbar)
  readonly foundation: readonly [CardPile, CardPile, CardPile, CardPile];
  readonly tableau: readonly [
    CardPile, CardPile, CardPile, CardPile,
    CardPile, CardPile, CardPile
  ];
  readonly moves: number;
  readonly elapsedSeconds: number;
  /** Anzahl der genutzten Undo-Aktionen. */
  readonly undoCount: number;
  /** Anzahl gescheiterter Doppelklicks (kein Ziel) — Basis für Shake-Penalty. */
  readonly shakeCount: number;
  /** Anzahl aufgedeckter verdeckter Tableau-Karten (manuell + autoFlip). */
  readonly flipCount: number;
  /** Anzahl der Stock-Umkehrungen (Waste → Stock). */
  readonly stockRecycleCount: number;
  readonly status: "idle" | "playing" | "won";
  readonly history: readonly Move[];  // Vollständige Zughistorie für Undo
}

// ============================================================
// Result<T, E> — sicheres Error-Handling ohne throw/catch
// ============================================================

export type Result<T, E = string> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });
export const err = <E = string>(error: E): Result<never, E> => ({ ok: false, error });
